import { Button, DateTimePicker, LinearProgress } from '@tunasong/ui-lib'
import { EntityEditor } from '@tunasong/editor'
import { graphHooks } from '@tunasong/graph-lib/react'
import { dayjs } from '@tunasong/models'
import { usePlugins } from '@tunasong/plugin-lib'
import type { CoreElement, Entity, Persisted } from '@tunasong/schemas'
import { SyncHandler } from '@tunasong/sync-lib'
import { Divider, Typography, VBox } from '@tunasong/ui-lib'
import type { Dayjs } from 'dayjs'
import type { FC, HTMLAttributes } from 'react'
import { useCallback, useEffect, useState } from 'react'
import invariant from 'tiny-invariant'

export interface TimeTravelSelectProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  entity?: Persisted<Entity>
  disableRestore?: boolean
  onChange(spec: { entity: Persisted<Entity>; travelTo: string }): void
}

export const TimeTravelSelect: FC<TimeTravelSelectProps> = props => {
  const { entity, disableRestore, onChange, ...boxProps } = props

  const plugins = usePlugins('all')

  const [restoreTo, setRestoreTo] = useState<string | null>(null)
  const { currentData: travelDoc, isLoading } = graphHooks.useEntityTimeTravel(
    { entityId: entity?.id ?? '', createdAtId: restoreTo ?? '' },
    { skip: !(entity && restoreTo) }
  )

  const [preview, setPreview] = useState<Persisted<Entity & CoreElement>>()

  useEffect(() => {
    const yDoc = travelDoc?.yDoc
    if (!(yDoc && restoreTo)) {
      return
    }

    /** Create a preview of the YDoc */
    const handler = SyncHandler.createFromYDocString({ entityId: travelDoc.id, yDoc })
    setPreview({
      ...travelDoc,
      children: handler.toJSON(),
    })
  }, [onChange, restoreTo, travelDoc])

  const handleRestore = useCallback(() => {
    invariant(travelDoc && restoreTo)
    onChange({ entity: travelDoc, travelTo: restoreTo })
  }, [onChange, restoreTo, travelDoc])

  const createdAtDate = dayjs(entity?.createdAt)

  const shouldDisableTime = (date: Dayjs) => date.toISOString() < createdAtDate.toISOString()
  const shouldDisableDate = (date: Dayjs) => date.endOf('day') < createdAtDate.endOf('day')
  const shouldDisableYear = (date: Dayjs) => {
    if (!entity?.createdAt) {
      return true
    }
    const year = new Date(entity.createdAt).getFullYear()
    return year > date.year()
  }

  const handleChange = (val: Dayjs | null) => {
    setRestoreTo(val ? val.toISOString() : null)
    setPreview(undefined)
  }

  return entity ? (
    <VBox {...boxProps}>
      <DateTimePicker
        disableFuture={true}
        disableHighlightToday={true}
        shouldDisableDate={shouldDisableDate}
        shouldDisableYear={shouldDisableYear}
        shouldDisableTime={shouldDisableTime}
        onAccept={handleChange}
      />
      <Button disabled={!preview || disableRestore} style={{ marginTop: 16 }} onClick={handleRestore}>
        Restore
      </Button>

      {preview && plugins ? (
        <>
          <Divider style={{ marginTop: 16 }}>Preview</Divider>
          <EntityEditor key={preview.updatedAt} readOnly={true} element={preview} plugins={plugins} />
        </>
      ) : null}
      {isLoading ? <LinearProgress /> : null}
    </VBox>
  ) : (
    <Typography>No data</Typography>
  )
}

export default TimeTravelSelect
